import type { CalendarEvent } from "@tellescope/types-models";

export interface VideoIntegration {
  hasTwilioVideo: boolean;
  hasChimeVideo: boolean;
  hasZoomVideo: boolean;
  hasExternalVideo: boolean;
  hasHealthieZoom: boolean;
  hasUserUrlVideo: boolean;
  hasAnyVideo: boolean;
  primaryVideoUrl?: string;
  videoType: "twilio" | "zoom" | "external" | "healthie" | null;
}

/**
 * Detect video integration type for a calendar event.
 * Ensures consistent video detection logic across all components.
 *
 * When the event has `useUserURL` set and the resolved host user has a personal
 * meeting URL, that URL takes highest priority and is surfaced as an external link.
 *
 * @param event - The calendar event to check for video integration
 * @param hostUserUrl - The host user's personal meeting URL (User.url), used when event.useUserURL is set
 * @returns Video integration details including type and availability
 *
 * @example
 * const videoIntegration = detectVideoIntegration(event);
 * if (videoIntegration.hasTwilioVideo) {
 *   // Show in-app video button
 * }
 */
export const detectVideoIntegration = (event: CalendarEvent, hostUserUrl?: string): VideoIntegration => {
  const hasUserUrlVideo = !!event.useUserURL && !!hostUserUrl;
  const hasTwilioVideo = event.videoIntegration === "Twilio" && !!event.enableVideoCall;
  const hasChimeVideo = !!event.enableVideoCall && !event.videoIntegration;
  const hasZoomVideo = event.videoIntegration === "Zoom" && !!event.videoURL;
  const hasExternalVideo = !!event.externalVideoURL;
  const hasHealthieZoom = !!event.healthieZoomJoinURL;

  const hasAnyVideo =
    hasUserUrlVideo || hasTwilioVideo || hasChimeVideo || hasZoomVideo || hasExternalVideo || hasHealthieZoom;

  const primaryVideoUrl = hasUserUrlVideo
    ? hostUserUrl
    : event.videoURL || event.externalVideoURL || event.healthieZoomJoinURL;

  let videoType: "twilio" | "zoom" | "external" | "healthie" | null = null;
  if (hasUserUrlVideo) videoType = "external";
  else if (hasTwilioVideo || hasChimeVideo) videoType = "twilio";
  else if (hasZoomVideo) videoType = "zoom";
  else if (hasExternalVideo) videoType = "external";
  else if (hasHealthieZoom) videoType = "healthie";

  return {
    hasTwilioVideo,
    hasChimeVideo,
    hasZoomVideo,
    hasExternalVideo,
    hasHealthieZoom,
    hasUserUrlVideo,
    hasAnyVideo,
    primaryVideoUrl,
    videoType,
  };
};
